import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { getLocalTxCodes } from "@/lib/localHistory";
import { STATUS_LABEL, CORRIDORS, formatAmount } from "@/lib/constants";
import { toast } from "sonner";
import { CheckCircle2, Clock, X, ArrowRight, Sparkles } from "lucide-react";
import CopyRefButton from "@/components/CopyRefButton";

const SEEN_KEY = "sl_active_tx_seen"; // map code → last known status (persistent)
const HIDDEN_KEY = "sl_active_tx_hidden"; // map code → true (per session)
const STATUS_ORDER = ["en_attente", "verifie", "envoye", "termine"];
const IN_PROGRESS = new Set(["en_attente", "verifie", "envoye"]);
const POLL_INTERVAL_MS = 30000;

const readJSON = (storage, key) => {
  try { return JSON.parse(storage.getItem(key) || "{}"); } catch (_e) { return {}; }
};
const writeJSON = (storage, key, value) => {
  try { storage.setItem(key, JSON.stringify(value)); } catch (_e) { /* ignore */ }
};

export default function ActiveTransactionBanner() {
  const { pathname } = useLocation();
  const [tx, setTx] = useState(null);

  // Hide the banner on the pages where it would be redundant or out of place.
  const disabled =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/agent/") ||
    pathname === "/suivi";

  const loadActive = useCallback(async () => {
    if (disabled) return;
    const codes = getLocalTxCodes();
    if (!codes.length) { setTx(null); return; }
    const hiddenMap = readJSON(sessionStorage, HIDDEN_KEY);
    // Walk through the most recent codes and pick the first in-progress one.
    for (const code of codes) {
      try {
        const { data } = await api.get(`/transactions/track/${code}`);
        if (!data) continue;
        const seen = readJSON(localStorage, SEEN_KEY);
        // Status changed since we last looked → notify (even if banner is hidden for this code)
        if (seen[code] && seen[code] !== data.status && data.status !== seen[code]) {
          const label = STATUS_LABEL[data.status] || data.status;
          if (data.status === "termine") {
            toast.success(`${code} · ${label}`, { description: "Votre transfert est terminé." });
          } else if (data.status === "annule") {
            toast.error(`${code} · ${label}`, { description: "Votre transaction a été annulée." });
          } else {
            toast.success(`${code} · ${label}`, { description: "Votre transaction a progressé." });
          }
        }
        seen[code] = data.status;
        writeJSON(localStorage, SEEN_KEY, seen);

        if (IN_PROGRESS.has(data.status) && !hiddenMap[code]) {
          setTx(data);
          return;
        }
      } catch (_e) { /* ignore one bad code, keep going */ }
    }
    setTx(null);
  }, [disabled]);

  // Initial load + revalidate on route change
  useEffect(() => { loadActive(); }, [loadActive]);

  // Poll periodically while tab visible
  useEffect(() => {
    if (disabled) return;
    let intervalId = null;
    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(loadActive, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        loadActive();
        startPolling();
      } else {
        stopPolling();
      }
    };
    const onFocus = () => loadActive();
    startPolling();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadActive, disabled]);

  if (disabled || !tx) return null;

  const dismiss = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const hidden = readJSON(sessionStorage, HIDDEN_KEY);
    hidden[tx.code] = true;
    writeJSON(sessionStorage, HIDDEN_KEY, hidden);
    setTx(null);
  };

  const corridor = CORRIDORS[tx.corridor || "tg_ga"];
  return (
    <div
      className="fixed top-16 inset-x-0 z-40 sl-fadeup"
      data-testid="active-tx-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3">
        <div className="rounded-2xl bg-white border border-[var(--sl-primary)]/20 shadow-[0_14px_36px_-12px_rgba(10,77,60,0.25)] overflow-hidden">
          {/* Top row */}
          <div className="px-4 sm:px-5 py-3 flex items-center gap-3 flex-wrap">
            <span className="w-8 h-8 rounded-full grid place-items-center text-white flex-shrink-0"
              style={{ background: "var(--sl-primary)" }}>
              <Sparkles size={14} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                  Transfert en cours
                </span>
                <span className="font-mono text-sm font-bold text-[var(--sl-primary)]" data-testid="active-tx-code">
                  {tx.code}
                </span>
                <CopyRefButton code={tx.code} compact />
                <span className="text-xs text-gray-500 hidden sm:inline">·</span>
                <span className="text-xs text-gray-600 hidden sm:inline">
                  {corridor?.short || corridor?.label}
                </span>
                <span className="text-xs text-gray-500 hidden md:inline">·</span>
                <span className="text-xs font-semibold text-gray-800 hidden md:inline">
                  {formatAmount(tx.amount_xof, tx.source_currency || "XOF")}
                </span>
              </div>
            </div>
            <Link
              to={`/suivi?code=${tx.code}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--sl-primary)] hover:underline"
              data-testid="active-tx-view"
            >
              Voir la fiche <ArrowRight size={12} />
            </Link>
            <button
              onClick={dismiss}
              className="text-gray-400 hover:text-gray-700 rounded p-1"
              aria-label="Masquer"
              data-testid="active-tx-dismiss"
            >
              <X size={14} />
            </button>
          </div>

          {/* Progress bar */}
          <StatusProgressBar status={tx.status} variant="banner" />
        </div>
      </div>
    </div>
  );
}

/* ----------------- Reusable progress bar ----------------- */
const STEP_DEFS = [
  { key: "en_attente", label: "En attente" },
  { key: "verifie", label: "Vérifié" },
  { key: "envoye", label: "Envoyé" },
  { key: "termine", label: "Terminé" },
];

export function StatusProgressBar({ status, variant = "default" }) {
  // If transaction was cancelled, show a single red bar.
  if (status === "annule") {
    return (
      <div className="px-4 sm:px-5 pb-3 sm:pb-4" data-testid="progress-bar-cancelled">
        <div className="h-1.5 rounded-full bg-red-200 relative overflow-hidden">
          <div className="absolute inset-0 bg-red-500" />
        </div>
        <div className="mt-2 text-xs text-red-700 font-medium">
          Transaction annulée
        </div>
      </div>
    );
  }
  const idx = STATUS_ORDER.indexOf(status);
  const pct = idx < 0 ? 0 : ((idx + 1) / STEP_DEFS.length) * 100;
  const isBanner = variant === "banner";
  return (
    <div className={isBanner ? "px-4 sm:px-5 pb-3 sm:pb-4" : ""} data-testid="progress-bar">
      <div className="relative">
        {/* Track */}
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, var(--sl-primary), var(--sl-secondary))",
            }}
          />
        </div>
        {/* Nodes */}
        <div className="absolute inset-0 flex items-center justify-between -mt-[3px]">
          {STEP_DEFS.map((s, i) => {
            const done = i < idx;
            const current = i === idx;
            return (
              <div
                key={s.key}
                className={`w-3.5 h-3.5 rounded-full border-2 ${
                  done
                    ? "bg-[var(--sl-primary)] border-[var(--sl-primary)]"
                    : current
                    ? "bg-white border-[var(--sl-secondary)] ring-4 ring-[var(--sl-secondary)]/25"
                    : "bg-white border-gray-300"
                }`}
                title={s.label}
              >
                {done && <CheckCircle2 size={10} className="text-white -mt-[1px] -ml-[1px]" />}
              </div>
            );
          })}
        </div>
      </div>
      {/* Labels */}
      <div className="mt-3 grid grid-cols-4 text-[10px] sm:text-xs">
        {STEP_DEFS.map((s, i) => {
          const done = i < idx;
          const current = i === idx;
          return (
            <div key={s.key} className={`flex flex-col items-center gap-0.5 ${i === 0 ? "items-start" : i === STEP_DEFS.length - 1 ? "items-end" : ""}`}>
              <span className={`${current ? "text-[var(--sl-secondary-dark)] font-semibold" : done ? "text-[var(--sl-primary)] font-medium" : "text-gray-400"}`}>
                {s.label}
              </span>
              {current && (
                <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                  <Clock size={9} /> en cours
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
